import { init as initMicroApps, initTrackers } from './init'
import { SPARKY_FRAMEWORK } from './initConst'
import { tryParseJson, isInSilentLoginIframe, overrideVars } from './initUtils'
import { __console, __isLocalhost } from './debug'
import { _timing, _timeouts, measureNetworkCondition, applyExtendedTimeouts } from './performance'
import libs from '../libs/base'
import moduleImport from '../libs/moduleImport'
import type { MicroAppRecord } from './init'

declare global {
  function setTimeout(callback: (...args: any[]) => void, ms: number, ...args: any[]): number
  function clearTimeout(timeoutId: number): void
}

export type SparkyInitDetails = {
  sparkyConfig: MicroAppRecord[]
  overrides?: MicroAppRecord[]
  overridable?: boolean
  allowList?: string
  prefetching?: string
}
export type FxScriptElement = HTMLScriptElement & { details?: SparkyInitDetails }
export type PromiseScriptElement = HTMLScriptElement & { promise?: Promise<any> }

const doc = window.document
const TIMEOUT = 'timeout'
const ESM = 'module'

const loadedPromise = (script: PromiseScriptElement) => {
  if (script.promise) {
    return script.promise.catch(err => err)
  }
  return new Promise((resolve) => {
    script.onload = resolve
    script.onerror = resolve
  })
}

const waitForLoad = (promises: Promise<any>[] | Promise<any>, timeout: number) => {
  return new Promise((resolve) => {
    if (Array.isArray(promises)) {
      Promise.all(promises).then(resolve, resolve)
    }
    else if (promises.then) {
      promises.then(resolve, resolve)
    }
    else {
      return resolve(undefined)
    }
    if (timeout > 0) {
      setTimeout(resolve, timeout, TIMEOUT)
    }
  })
}

export type ArrayWithKeys<T = any> = T[] & { [key: string]: any }
const getAppAllowList = (allowList: string | undefined): ArrayWithKeys<string> => {
  const list = allowList?.replace(/\s/g, '')
  if (!list) {
    return [] as ArrayWithKeys<string>
  }
  return list.split(',').reduce((arr, name: string) => {
    if (name && !arr[name]) {
      arr.push(name)
      arr[name] = 1
    }
    return arr
  }, [] as ArrayWithKeys<string>) // ensure uniq and framework first
}

const expendMicroApp = (microApp: MicroAppRecord) => {
  if (microApp.baseUrl) {
    const concatUrl = (url: string) => {
      if (/^https?:\/\//i.test(url)) {
        return url
      }
      return new URL(url, microApp.baseUrl).toString()
    }
    microApp.entry = microApp.entry && concatUrl(microApp.entry)
    if (Array.isArray(microApp.assets)) {
      microApp.assets = microApp.assets.map(concatUrl)
      if (microApp.entry && microApp.assets.includes(microApp.entry)) {
        microApp.assets.unshift(microApp.entry)
      }
    }
    if (Array.isArray(microApp.dependencies)) {
      microApp.dependencies = microApp.dependencies.map(concatUrl)
    }
  }
  return microApp
}

const COPY_MFE_FIELDS = [
  'id', 'name', 'entry', 'exports',
  'dependencies', 'generated_id', 'entry_type',
  'version', 'ci_build', 'updated',
  '+variables', 'flags',
] as (keyof MicroAppRecord)[]
const getOverrides = (overrides: MicroAppRecord[]) => {
  if (!Array.isArray(overrides) || !overrides.length) {
    return []
  }
  return overrides.reduce((arr, app: MicroAppRecord) => {
    const id = app.id || app.name
    if (id && (app.entry || app.variables || app['+variables'])) {
      try {
        const cfg = COPY_MFE_FIELDS.reduce((acc, field) => {
          if (app[field] !== undefined) {
            acc[field] = app[field]
          }
          return acc
        }, {} as MicroAppRecord)
        if (app.is_framework || app.name === SPARKY_FRAMEWORK) {
          cfg.is_framework = true
        }
        const variables = app.variables || (app.varsJson && JSON.parse(app.varsJson))
        if (variables) {
          cfg.variables = variables
        }

        if (cfg.entry?.startsWith('/')) {
          cfg.entry = new URL(cfg.entry, window.location.origin).toString()
        }
        if (arr[id]) { // merge overrides
          for (const key of Object.keys(cfg)) {
            const val = (cfg as any)[key]
            // ignore null values, undefined already filtered
            if (val !== null) {
              arr[id][key] = val
            }
          }
          if (cfg.entry && !cfg.assets) {
            cfg.assets = undefined // intentionally remove assets if entry changed
          }
        }
        else {
          arr.push(cfg)
          arr[id] = cfg
          // arr[`name:${app.name}`] = cfg
        }
      }
      catch (err) {
        console.error('failed to parse micro-app varsJson', app.varsJson, err)
      }
    }
    return arr
  }, [] as ArrayWithKeys<MicroAppRecord>)
}

const mergeOverridesFilterSort = (microApps: MicroAppRecord[], overridesArr: MicroAppRecord[], allowListStr?: string) => {
  const overrides = getOverrides(overridesArr) as ArrayWithKeys<MicroAppRecord & { used?: true }>
  const allowList = getAppAllowList(allowListStr)
  const isAllow = allowList.length ? (app: MicroAppRecord) => allowList[app.id || app.name] || app.is_framework : () => true
  // apply overrides and filter
  for (const app of microApps) {
    app.id = app.id || app.name
    const override = overrides.length && isAllow(app) && overrides[app.id] as MicroAppRecord
    let variables = app.variables || tryParseJson(app.varsJson) as Record<string, any> | undefined || undefined
    if (override) { // merge
      if (override.entry && app.assets) {
        override.assets = undefined
      }
      if (override.entry_type === ESM && override.exports) {
        override.exports = undefined // remove exports if entry_type is module
      }
      Object.assign(app, override)
      app.variables = override.variables ? Object.assign(variables || {}, override.variables) : variables
      const varsOverride = app['+variables']
      if (varsOverride) {
        variables = overrideVars(variables, varsOverride)
        delete app['+variables']
      }
      // keep before completely deprecate data-vars
      app.varsJson = variables && Object.keys(variables).length ? JSON.stringify(variables) : undefined
      override.used = true
    }
    else if (variables && !app.variables) {
      app.variables = variables
    }
  }
  microApps = microApps.concat(overrides.filter(app => !app.used && isAllow(app)))
  if (allowList.length) { // filter and order by allow list
    const indexed = microApps.reduce((o, app: MicroAppRecord) => {
      if (app.is_framework) {
        o.is_framework = app
      }
      else {
        o[app.id] = app
        if (!o[app.name]) {
          o[app.name] = app
        }
      }
      return o
    }, {} as Record<string, MicroAppRecord>)
    microApps = allowList.reduce((arr, id) => {
      const app = indexed[id]
      if (app && !app.is_framework) {
        arr.push(indexed[id])
      }
      return arr
    }, [indexed.is_framework])
  }
  overrides.length = 0 // release
  return microApps
}

const detectUrlAssetType = (url: string): string => {
  if (/\.js(?:$|[?#])/.test(url)) {
    return 'script'
  }
  if (/[/.]css(?:$|[?#])/.test(url)) {
    return 'style'
  }
  return ''
}

const PRELOAD_FONTS_REGEX = /\/lato-(?:regular|italic|bold|semibold|medium)\./
const prefetchMicroApps = (microApps: MicroAppRecord[]) => {
  if (sessionStorage.__sparky_prefetched__) {
    return
  }
  const preloadFrag = doc.createDocumentFragment()
  const prefetchFrag = doc.createDocumentFragment()
  const delayPrefetchFrag = doc.createDocumentFragment()
  const exists = {} as Record<string, boolean>
  for (const app of microApps) {
    if (app.entry && !app.is_framework) {
      const isEsm = app.entry_type === ESM
      const preloadUrls = (isEsm ? [] : [app.entry]).concat(app.dependencies || [])
      if (isEsm && !exists[app.entry]) {
        const preloadLink = doc.createElement('link')
        preloadLink.href = app.entry
        preloadLink.rel = 'modulepreload'
        preloadFrag.appendChild(preloadLink)
        exists[app.entry] = true
      }
      for (const url of preloadUrls) {
        if (exists[url]) {
          continue
        }
        const asType = detectUrlAssetType(url)
        if (asType) {
          const preloadLink = doc.createElement('link')
          preloadLink.href = url
          preloadLink.rel = 'preload'
          preloadLink.as = asType
          preloadFrag.appendChild(preloadLink)
          exists[url] = true
        }
      }
    }
    if (app.prefetch_assets && Array.isArray(app.assets)) {
      const regex = app.prefetch_assets === true ? /\.(?:js|css)(?:$|[?#])/ : new RegExp(app.prefetch_assets)
      for (const url of app.assets) {
        if (exists[url]) {
          continue
        }
        const link = doc.createElement('link')
        link.href = url
        if (regex.test(url) && url !== app.entry) {
          link.rel = app.is_framework ? 'preload' : 'prefetch'
          link.as = url.includes('.css') ? 'style' : 'script'
          if (app.is_framework) {
            preloadFrag.appendChild(link)
          }
          else {
            prefetchFrag.appendChild(link)
          }
          exists[url] = true
        }
        else if (/\.woff2(?:$|[?#])/.test(url)) {
          link.rel = 'prefetch' // always use prefetch in this case
          link.as = 'font'
          link.type = 'font/woff2'
          link.crossOrigin = 'anonymous'
          if (PRELOAD_FONTS_REGEX.test(url)) {
            prefetchFrag.appendChild(link)
          }
          else {
            delayPrefetchFrag.appendChild(link)
          }
          // prefetchFrag.appendChild(link)
          exists[url] = true
        }
      }
    }
  }
  doc.head.appendChild(preloadFrag)
  doc.head.appendChild(prefetchFrag)
  if (delayPrefetchFrag.childElementCount > 0) {
    setTimeout(() => {
      requestIdleCallback(() => {
        doc.head.appendChild(delayPrefetchFrag)
        clearPreLinks()
      }, { timeout: 10000 })
    }, 3000)
  }
  sessionStorage.__sparky_prefetched__ = 1
}

type FnWithTs = (() => void) & { _ts?: number }
const clearPreLinks: FnWithTs = () => {
  if (clearPreLinks._ts) {
    window.clearTimeout(clearPreLinks._ts)
  }
  clearPreLinks._ts = window.setTimeout(() => { // cleanup all preload/prefetch/preconnect links
    // browser will not cancel the preload/prefetch/preconnect requests
    doc.head.querySelectorAll<HTMLLinkElement>(
      'link[rel=preconnect],link[rel=preload],link[rel=prefetch]'
    ).forEach((link) => {
      doc.head.removeChild(link)
    })
  }, 5000)
}

const convertEp = (ep: any) => {
  if (ep.default) {
    if (ep.exposes) {
      ep.default.exposes = ep.exposes
    }
    if (ep.extensions) {
      ep.default.extensions = ep.extensions
    }
    if (ep.extensionPoints) {
      ep.default.extensionPoints = ep.extensionPoints
    }
    return ep.default
  }
  return ep
}

const loadMicroApps = (microApps: MicroAppRecord[], fxScript: FxScriptElement, fxVars?: Record<string, any>) => {
  const cssTags = {} as Record<string, HTMLStyleElement>
  const jsTags = {} as Record<string, HTMLScriptElement>
  const appsTags = [] as HTMLScriptElement[]
  const starts = Date.now()
  _timing.micro_apps_assets_load_start = starts
  if (fxVars?.pdf_mode) {
    applyExtendedTimeouts()
  }
  else {
    measureNetworkCondition()
  }

  const loadFullFx = async () => {
    if (!isInSilentLoginIframe) {
      await import('../app/app')
      const timing = Date.now()
      _timing.framework_load_end = timing
      _timing.framework_load_duration = _timing.framework_load_end - starts
      __console.info(`framework loaded in ${_timing.framework_load_duration}ms`)
      __console.info('sparky libs versions', libs.versions)
      if (!__isLocalhost) {
        window.DD_RUM?.onReady(() => { // not using getDatadogRum to avoid circular ref
          const ddRum = window.DD_RUM!
          ddRum.addTiming('sparky_framework_loaded', timing)
          if (_timing.shell_loader_start) {
            ddRum.addTiming('sparky_shell_loader_start', _timing.shell_loader_start)
          }
          if (_timing.framework_init_start) {
            ddRum.addTiming('sparky_framework_init_start', _timing.framework_init_start)
          }
        })
      }
    }
  }
  const fxPromise = loadFullFx()

  const markEmbeddedScript = (app: MicroAppRecord) => {
    const script: HTMLScriptElement = fxScript ||
      doc.querySelector(`script[data-micro-app='${app.id || app.name}']`) ||
      doc.currentScript
    if (!script.getAttribute('data-micro-app')) {
      script.setAttribute('data-micro-app', app.id || app.name)
    }
    if (app.exports) {
      script.setAttribute('data-global-var', app.exports)
    }
    if (app.varsJson) { // keep before completely deprecate data-vars
      script.setAttribute('data-app-vars', app.varsJson)
    }
  }

  const fallbackMicroApps = window.__SPARKY_LIBS__?.__MICRO_APPS__
  for (const app of microApps) {
    if (!app.entry) {
      if (!app.is_framework) {
        console.error('micro-app missing entry', app)
      }
      continue
    }
    if (Array.isArray(app.dependencies)) {
      for (const url of app.dependencies) {
        const assetType = detectUrlAssetType(url)
        if (assetType === 'script') {
          if (doc.documentElement.querySelector(`script[src="${url}"]`)) {
            continue
          }
          const script = doc.createElement('script')
          script.src = url
          script.type = 'text/javascript'
          script.async = false // sync load
          jsTags[url] = script
        }
        else if (assetType === 'style') {
          if (app.is_framework && /\/load\w+(?:\.\w+)?\.css$/i.test(url) &&
            Array.from(document.styleSheets).some(sheet => sheet.href === url)) {
            continue // loading css already loaded
          }
          if (doc.documentElement.querySelector(`link[href="${url}"][rel=stylesheet]`)) {
            continue
          }
          const link = doc.createElement('link')
          link.type = 'text/css'
          link.rel = 'stylesheet'
          link.href = url
          cssTags[url] = link
        }
      }
    }
    if (app.is_framework) { // framework already loaded
      markEmbeddedScript(app)
    }
    else if (app.entry_type === ESM) { // for now esm loader always load without wait for deps except for framework
      const waitPromise = app.id === 'sparky-one-nav' ? Promise.resolve() : fxPromise
      app.promise = waitPromise.then(() => moduleImport(app.entry)).then((module) => {
        if (!module?.name || !module.id) {
          console.error('module based micro-app not exported correctly, please check your build config', module, app)
          return undefined
        }
        if (module.id !== app.id || module.name !== app.name) {
          console.error('module based micro-app exported id or name is not matched', module, app)
          return undefined
        }
        app.ep = convertEp(module)
        delete app.promise
        const ends = Date.now()
        _timing.micro_apps[app.id || app.name] = {
          entry_load_start: starts,
          entry_load_end: ends,
          entry_load_duration: ends - starts,
        }
        return app.ep
      }, (error: Error) => {
        console.error(`failed to import module micro-app ${app.id || app.name} entry`, error)
        app.error = error
        delete app.promise
        return undefined
      })
    }
    else {
      const script = doc.createElement('script') as PromiseScriptElement & {
        microAppId: string
        generatedId?: string
        appName: string
        __ep: any
        error?: Error
        loaded?: boolean
      }
      script.src = app.entry
      script.type = 'text/javascript'
      const id = app.id || app.name
      script.microAppId = id
      script.appName = app.name
      script.generatedId = app.generated_id
      script.setAttribute('data-micro-app', id)
      if (app.exports) {
        script.setAttribute('data-global-var', app.exports)
      }
      if (app.varsJson) { // keep before completely deprecate data-vars
        script.setAttribute('data-app-vars', app.varsJson)
      }
      // script.app = app
      script.promise = new Promise((resolve, reject) => {
        let ts = _timeouts.microAppEntryLoadTimeout > 0 ? window.setTimeout(() => {
          (script as any).timeout = true
          ts = 0
          // app.timeout = true
          console.error(`micro-app ${id} load timeout after ${Math.round(_timeouts.microAppEntryLoadTimeout / 1000)}s`)
          reject(new Error(TIMEOUT))
        }, _timeouts.microAppEntryLoadTimeout) : 0
        const clearTs = () => {
          ts = (ts && clearTimeout(ts)) || 0
        }
        const onEp = (e?: Event) => {
          const ep = (e as CustomEvent)?.detail || (app.exports && (window as any)[app.exports]) || script.__ep ||
            fallbackMicroApps?.[id] || (app.name !== id ? fallbackMicroApps?.[app.name] : undefined)
          if (ep) {
            app.ep = convertEp(ep)
            app.loaded = true
            // __console.info('micro-app loaded', app.id, app)
          }
          else {
            __console.error('micro-app loaded without ep', app.id, app)
            app.error = new Error('micro-app loaded without ep')
          }
          script.loaded = true
          resolve(app)
          cleanup()
        }
        const cleanup = () => {
          clearTs()
          script.onload = null
          script.onerror = null
          script.removeEventListener('micro-app-ep', onEp)
          if (script.__ep) {
            delete script.__ep
          }
          const microApps = window.__SPARKY_LIBS__?.__MICRO_APPS__
          if (microApps?.[id]) {
            microApps[id] = true
          }
          delete script.promise
          delete app.promise
        }
        script.addEventListener('micro-app-ep', onEp, { once: true })
        script.onload = () => {
          clearTs()
          onEp()
        }
        script.onerror = (event) => {
          const error = new Error(`failed to load micro-app ${id} entry`)
          Object.assign(error, {
            app: id,
            cause: event,
          })
          app.error = error
          script.error = error
          // __console.info('micro-app load failed', app.id, app, error)
          clearTs()
          reject(error)
          cleanup()
        }
      })
      app.promise = script.promise.then((app) => {
        const ends = Date.now()
        _timing.micro_apps[app.id || app.name] = {
          entry_load_start: starts,
          entry_load_end: ends,
          entry_load_duration: ends - starts,
        }
        return app.ep
      })

      appsTags.push(script)
    }
  }
  // const microAppGlobalVars = microApps.reduce((names: string[], app: MicroAppRecord) => {
  //   if (app.exports) {
  //     names.push(app.exports)
  //   }
  //   return names
  // }, [] as string[])
  // let appsPromise
  const depsPromises = [] as Promise<any>[]
  if (fxPromise) {
    depsPromises.push(fxPromise)
  }
  const frag = doc.createDocumentFragment()
  for (const key of Object.keys(cssTags)) {
    frag.appendChild(cssTags[key])
  }
  for (const key of Object.keys(jsTags)) {
    const script = jsTags[key]
    frag.appendChild(script)
    depsPromises.push(loadedPromise(script))
  }
  doc.head.appendChild(frag)
  // const isReady = (res: any) => (
  //   res !== TIMEOUT || microAppGlobalVars.every((name: string) => (window as any)[name])
  // )
  return waitForLoad(depsPromises, _timeouts.microAppDepsLoadTimeout).then(async () => {
    // wait for js dependencies and fx full load
    const appsPromises = []
    const frag = doc.createDocumentFragment()
    for (const tag of appsTags) {
      frag.appendChild(tag)
      appsPromises.push(loadedPromise(tag))
    }
    doc.body.appendChild(frag)
    if (!isInSilentLoginIframe) {
      Promise.allSettled(appsPromises).then(() => {
        _timing.micro_apps_assets_load_end = Date.now()
        _timing.micro_apps_assets_load_duration = _timing.micro_apps_assets_load_end - starts
        __console.info('micro-apps', microApps, `loaded in ${_timing.micro_apps_assets_load_duration}ms`)
      })
    }
    return microApps
  })
}

const init = ({ sparkyConfig, overrides = [], allowList, prefetching, overridable, target, _shell_loader_start }: SparkyInitDetails & { target: FxScriptElement, _shell_loader_start?: number }) => {
  if (!overridable && (overrides?.length || allowList)) {
    console.error(`the current sparky shell ${window.location.origin} is not overridable, ignore following overrides:`, allowList || '', overrides)
    allowList = ''
    overrides = []
  }
  if (_shell_loader_start) {
    _timing.shell_loader_start = _shell_loader_start
  }
  _timing.framework_init_start = Date.now()
  const microApps = mergeOverridesFilterSort(sparkyConfig.map(expendMicroApp), overrides, isInSilentLoginIframe ? SPARKY_FRAMEWORK : allowList)
  __console.info('init event', sparkyConfig, overrides, allowList, microApps)
  const fxVars = microApps[0].variables
  if (overridable) {
    Object.defineProperty(fxVars, 'overridable', { value: true })
  }
  if (__isLocalhost) {
    Object.defineProperty(fxVars, 'localhost', { value: true })
  }
  initTrackers(fxVars)
  if (!prefetching && !isInSilentLoginIframe) {
    prefetchMicroApps(microApps)
  }
  loadMicroApps(microApps, target, fxVars)
  initMicroApps(microApps)
  clearPreLinks()
}

const loader = (fxScript?: FxScriptElement) => {
  if (fxScript?.details?.sparkyConfig) {
    const details = Object.assign(fxScript.details, { target: fxScript })
    fxScript.details = undefined
    return init(details)
  }
  return console.error('failed to load sparky config, shell-loader is required', fxScript?.details)
}

export default loader
